// src/components/tactic/LanePickerDialog.jsx
// Asks which lane a hero is going to before dropping it on the board.
//
// The board used to guess: most-played lane, or the first free one. That is a
// fine default and it is still the one highlighted here — but a coach placing a
// hero usually already knows where they want them, and a wrong guess costs a
// drag to undo. So the guess became a suggestion.

import React, { useEffect } from "react";
import { getHeroImageUrlWithFallback } from "../../utils/heroCatalog";
import { LANES, pickCount } from "../../utils/tactic/heroLanes";
import { TEAM_COLORS } from "./TacticCanvas";
import { Btn, Label, Row, T } from "./tacticUi";

const LANE_LABELS = {
  DSL: "ออฟเลน",
  JUG: "ป่า",
  MID: "มิด",
  ADL: "แครี่",
  ROAM: "โรม",
};

export default function LanePickerDialog({
  heroName,
  team,
  suggested,
  takenLanes,
  laneIndex,
  onPick,
  onTeamChange,
  onCancel,
}) {
  // Esc closes; 1–5 pick a lane without leaving the keyboard.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
        return;
      }
      const n = Number(e.key);
      if (n >= 1 && n <= LANES.length) {
        e.preventDefault();
        onPick(LANES[n - 1]);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onPick, onCancel]);

  const teamColor = TEAM_COLORS[team];

  return (
    <div
      onPointerDown={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(4,6,10,0.72)",
        display: "grid",
        placeItems: "center",
        zIndex: 80,
        padding: 16,
      }}
    >
      <div
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          background: T.panelSolid,
          border: `1px solid ${T.line}`,
          borderRadius: 14,
          padding: 18,
          width: "min(400px, 100%)",
          boxShadow: "0 24px 70px rgba(0,0,0,0.6)",
        }}
      >
        <Row gap={11} style={{ marginBottom: 14 }}>
          <span
            style={{
              width: 52,
              height: 52,
              borderRadius: 10,
              overflow: "hidden",
              flexShrink: 0,
              border: `2px solid ${teamColor}`,
              background: "#0b0d12",
            }}
          >
            <img
              src={getHeroImageUrlWithFallback(heroName)}
              alt={heroName}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </span>
          <span style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{heroName}</div>
            <div style={{ fontSize: 11.5, color: T.faint }}>วางลงเลนไหน</div>
          </span>
        </Row>

        <Label>ทีม</Label>
        <Row gap={6} style={{ marginBottom: 12 }}>
          <Btn tone="blue" active={team === "blue"} onClick={() => onTeamChange("blue")} style={{ flex: 1 }}>
            ฝั่งบลู
          </Btn>
          <Btn tone="red" active={team === "red"} onClick={() => onTeamChange("red")} style={{ flex: 1 }}>
            ฝั่งเรด
          </Btn>
        </Row>

        <Label>ตำแหน่ง</Label>
        <div style={{ display: "grid", gap: 6 }}>
          {LANES.map((lane, i) => {
            const taken = takenLanes.has(lane);
            const plays = pickCount(laneIndex, heroName, lane);
            return (
              <Btn
                key={lane}
                active={lane === suggested}
                onClick={() => onPick(lane)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  justifyContent: "flex-start",
                  padding: "9px 11px",
                }}
              >
                <span style={{ color: T.faint, fontFamily: T.mono, fontSize: 11 }}>{i + 1}</span>
                <span style={{ minWidth: 62, textAlign: "left" }}>{LANE_LABELS[lane]}</span>
                <span style={{ color: T.faint, fontSize: 11 }}>{lane}</span>

                <span style={{ marginLeft: "auto", display: "inline-flex", gap: 8, alignItems: "center" }}>
                  {plays > 0 && (
                    <span style={{ color: "#ffb182", fontSize: 11 }}>เล่นมา {plays} ครั้ง</span>
                  )}
                  {taken && <span style={{ color: T.dim, fontSize: 11 }}>มีคนแล้ว</span>}
                </span>
              </Btn>
            );
          })}
        </div>

        <Row gap={8} style={{ marginTop: 14 }}>
          <span style={{ fontSize: 11, color: T.faint, lineHeight: 1.6 }}>
            {suggested
              ? `แนะนำ ${LANE_LABELS[suggested]} — เลนที่ทีมเล่นฮีโร่ตัวนี้บ่อยที่สุด`
              : "ยังไม่มีสถิติของฮีโร่ตัวนี้ในไฟล์"}
          </span>
          <Btn tone="ghost" onClick={onCancel} style={{ marginLeft: "auto" }}>
            ยกเลิก
          </Btn>
        </Row>
      </div>
    </div>
  );
}
